/**
 * File Upload Service
 * 
 * Handles file uploads using presigned URLs to Backblaze B2 (or other S3-compatible storage).
 * 
 * Flow:
 * 1. Get presigned URL from backend
 * 2. Upload file directly to cloud storage
 * 3. Confirm upload with backend to save record
 */

import axios from 'axios';
import { getBackendUrl } from '@/config';

const API_URL = getBackendUrl();

/**
 * Get auth headers for API requests
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return {
    'Authorization': `Bearer ${token}`
  };
};

/**
 * Upload a single file using presigned URL
 * 
 * @param {File} file - The file to upload
 * @param {string} entityType - Type of entity (tax_filing, llc_formation)
 * @param {string} entityId - UUID of the entity
 * @param {string} documentType - Type of document (w2, id_front, articles_of_organization, etc.)
 * @param {Function} onProgress - Optional progress callback (0-100)
 * @returns {Promise<Object>} - Upload result with file URL
 */
export const uploadFile = async (file, entityType, entityId, documentType, onProgress = null) => {
  try {
    // Use backend proxy upload (bypasses CORS issues with B2/S3)
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', entityType);
    formData.append('entity_id', entityId);
    formData.append('document_type', documentType);

    const response = await axios.post(
      `${API_URL}/api/upload-document`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percentCompleted);
          }
        }
      }
    );

    return {
      success: true,
      data: response.data
    };

  } catch (error) {
    console.error('Upload error:', error);
    return {
      success: false,
      error: error.response?.data?.detail || error.message || 'Upload failed'
    };
  }
};

/**
 * Upload multiple files
 * 
 * @param {Array<{file: File, documentType: string}>} files - Array of files with their document types
 * @param {string} entityType - Type of entity
 * @param {string} entityId - UUID of the entity
 * @param {Function} onFileProgress - Optional callback (fileIndex, progress)
 * @returns {Promise<Array>} - Array of upload results
 */
export const uploadFiles = async (files, entityType, entityId, onFileProgress = null) => {
  const results = [];

  for (let i = 0; i < files.length; i++) {
    const { file, documentType } = files[i];
    
    const result = await uploadFile(
      file,
      entityType,
      entityId,
      documentType,
      (progress) => {
        if (onFileProgress) {
          onFileProgress(i, progress);
        }
      }
    );

    results.push({
      ...result,
      fileName: file.name,
      documentType
    });
  }

  return results;
};

/**
 * Get documents for an entity
 * 
 * @param {string} entityType - Type of entity
 * @param {string} entityId - UUID of the entity
 * @returns {Promise<Array>} - Array of documents
 */
export const getDocuments = async (entityType, entityId) => {
  try {
    const response = await axios.get(
      `${API_URL}/api/documents/${entityType}/${entityId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching documents:', error);
    return [];
  }
};

/**
 * Delete a document
 * 
 * @param {string} documentId - UUID of the document
 * @returns {Promise<boolean>} - Success status
 */
export const deleteDocument = async (documentId) => {
  try {
    await axios.delete(
      `${API_URL}/api/documents/${documentId}`,
      { headers: getAuthHeaders() }
    );
    return true;
  } catch (error) {
    console.error('Error deleting document:', error);
    return false;
  }
};

/**
 * Check upload status/capability
 * 
 * @returns {Promise<Object>} - Upload status info
 */
export const getUploadStatus = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/upload/status`);
    return response.data;
  } catch (error) {
    console.error('Error checking upload status:', error);
    return {
      object_storage_configured: false,
      warning: 'Could not check upload status'
    };
  }
};

export default {
  uploadFile,
  uploadFiles,
  getDocuments,
  deleteDocument,
  getUploadStatus
};
